import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const HybridComparisonSection = () => {
  const cloudBenefits = [
    {
      icon: "Cloud",
      title: "Real-Time Sync",
      description: "Syncs with your online store when using Cultaz Cloud - keeps your cloud catalog and website up to date. Note: Cultaz does not provide cross-location (multi-store) syncing."
    },
    {
      icon: "BarChart3",
      title: "Advanced Analytics",
      description: "Access comprehensive reports and insights from anywhere, anytime"
    },
    {
      icon: "Users",
      title: "Multi-User Access",
      description: "Team collaboration with role-based permissions and activity tracking"
    },
    {
      icon: "Smartphone",
      title: "Mobile Ready",
      description: "Manage your business on-the-go with responsive web interface"
    }
  ];

  const offlineBenefits = [
    {
      icon: "Wifi",
      title: "Zero Downtime",
      description: "Continue operations even without internet connectivity"
    },
    {
      icon: "HardDrive",
      title: "Local Data Storage",
      description: "All your data stored securely on your computer with manual backups trigger"
    },
    {
      icon: "Zap",
      title: "Lightning Fast",
      description: "No lag or delays - instant transaction processing"
    },
    {
      icon: "Shield",
      title: "Complete Control",
      description: "Monthly billing options with local data ownership and full control"
    }
  ];

  return (
    <section className="py-8 md:py-12 lg:py-16 bg-muted/30">
      <div className="container mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            The Hybrid Advantage
          </h2>
          <p className="font-body text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Choose the solution that matches your business needs - or use both for ultimate flexibility
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-6 md:p-8 lg:p-10 border border-primary/20 shadow-purple-md hover:shadow-purple-lg transition-smooth"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="flex h-14 w-14 md:h-16 md:w-16 items-center justify-center rounded-xl bg-primary/10">
                <Icon name="Cloud" size={32} color="var(--color-primary)" strokeWidth={2} />
              </div>
              <div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Cloud-Based</h3>
                <p className="font-body text-sm text-muted-foreground">Perfect for online stores and remote management</p>
              </div>
            </div>

            <div className="space-y-4">
              {cloudBenefits?.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex gap-4 p-4 bg-card rounded-lg border border-border hover:border-primary/30 transition-smooth"
                >
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <Icon name={benefit?.icon} size={20} color="var(--color-primary)" strokeWidth={2} />
                  </div>
                  <div>
                    <h4 className="font-body text-base font-semibold text-foreground mb-1">{benefit?.title}</h4>
                    <p className="font-body text-sm text-muted-foreground">{benefit?.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-accent/5 to-primary/5 rounded-2xl p-6 md:p-8 lg:p-10 border border-accent/20 shadow-purple-md hover:shadow-purple-lg transition-smooth"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="flex h-14 w-14 md:h-16 md:w-16 items-center justify-center rounded-xl bg-accent/10">
                <Icon name="HardDrive" size={32} color="var(--color-accent)" strokeWidth={2} />
              </div>
              <div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Offline POS</h3>
                <p className="font-body text-sm text-muted-foreground">Offline-first reliability - runs without internet and supports local networked cashiers</p>
              </div>
            </div>

            <div className="space-y-4">
              {offlineBenefits?.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex gap-4 p-4 bg-card rounded-lg border border-border hover:border-accent/30 transition-smooth"
                >
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-accent/10">
                    <Icon name={benefit?.icon} size={20} color="var(--color-accent)" strokeWidth={2} />
                  </div>
                  <div>
                    <h4 className="font-body text-base font-semibold text-foreground mb-1">{benefit?.title}</h4>
                    <p className="font-body text-sm text-muted-foreground">{benefit?.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HybridComparisonSection;